/**
 * gift-box-notice.js
 * Cart validation for Little Koha's "Build Your Own Gift Box" feature.
 *
 * Logic:
 * - If cart contains "Build Your Own" product type items (individual gift fillers),
 *   it checks that the gift box container product is also in the cart.
 * - If the box is missing, or the minimum item count isn't met, checkout is blocked
 *   and a modal is shown prompting the customer to add the box.
 *
 * Depends on:
 * - window.theme.gift_box (set by gift-box-notice.liquid)
 * - Canopy's <modal-dialog> custom element (modal.js)
 * - The gift-box-notice.liquid snippet being rendered in theme.liquid
 *
 * Gift message persistence strategy:
 * - localStorage ('lk_gift_message') is the single source of truth
 * - savedGiftMessage mirrors localStorage in memory for fast access
 * - Cart attribute ('Gift Message') is saved async for order admin display
 */

(function () {
  'use strict';

  // ─── In-memory gift message store ───────────────────────────────────────────
  // Initialise from localStorage immediately so it's available before init()
  let savedGiftMessage = localStorage.getItem('lk_gift_message') || '';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error(res.status);
      return res.json();
    });
  }

  function saveGiftMessageAttribute(value) {
    // localStorage is synchronous — always up to date immediately
    localStorage.setItem('lk_gift_message', value);
    savedGiftMessage = value;
    // Cart attribute save is async — used for order admin display
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Gift Message': value } })
    });
  }

  function openGiftBoxModal() {
    const modal = document.getElementById('gift-box-notice-modal');
    const trigger = document.querySelector('.gift-box-notice-trigger');
    if (modal && typeof modal.open === 'function') {
      modal.open(trigger || document.body);
    }
  }

  function validateCart(cart) {
    const giftBoxIds = [window.theme.gift_box && window.theme.gift_box.id];
    const modalInner = document.querySelector('.gift-box-notice-modal--inner');
    const minimumGiftItems = modalInner
      ? parseInt(modalInner.getAttribute('data-gift-min-items'), 10) || 0
      : 0;

    let requiresBox = false;
    let hasBox = false;
    let hasCustomBox = false;
    let giftItemQty = 0;

    cart.items.forEach(function (item) {
      const productType = (item.product_type || '').toLowerCase();

      if (
        productType.indexOf('build your own') !== -1 &&
        !giftBoxIds.includes(String(item.product_id))
      ) {
        requiresBox = true;
        giftItemQty += item.quantity;
      }

      if (productType.indexOf('gift box') !== -1) {
        hasBox = true;
      } else if (giftBoxIds.includes(String(item.product_id))) {
        hasCustomBox = true;
        hasBox = true;
      }
    });

    if ((requiresBox || hasCustomBox) && giftItemQty < minimumGiftItems) {
      return 'too few items';
    }

    if (requiresBox && !hasBox) {
      return 'no gift box';
    }

    return true;
  }

  function showValidationModal(reason) {
    const modal = document.getElementById('gift-box-notice-modal');
    if (!modal) return;

    const qtyError = modal.querySelector('.gift-box-notice-modal--qty-error');
    const boxError = modal.querySelector('.gift-box-notice-modal--box-error');
    const products = modal.querySelector('.gift-box-notice-modal--products');
    const smallProduct = modal.querySelector('.gift-box-notice-modal--product-small');

    if (reason === 'too few items') {
      if (qtyError) qtyError.style.display = 'block';
      if (boxError) boxError.style.display = 'none';
      if (products) products.style.display = 'none';
    } else {
      if (qtyError) qtyError.style.display = 'none';
      if (boxError) boxError.style.display = 'block';
      if (products) products.style.display = 'grid';
      if (smallProduct) {
        smallProduct.style.display = reason === 'no large gift box' ? 'none' : 'block';
      }
    }

    openGiftBoxModal();
  }

  // ─── Repopulate any visible gift message field ───────────────────────────────

  function repopulateGiftMessageFields() {
    if (savedGiftMessage === '') return;

    const cartPageField = document.getElementById('cart-gift-message');
    if (cartPageField) cartPageField.value = savedGiftMessage;

    const drawerField = document.getElementById('cart-drawer-gift-message');
    if (drawerField) drawerField.value = savedGiftMessage;
  }

  // ─── Cart page checkout handler ──────────────────────────────────────────────

  function checkoutHandler(evt) {
    const cartForm = document.getElementById('cart');

    if (cartForm && !cartForm.checkValidity()) {
      cartForm.reportValidity();
      return;
    }

    const giftMessage = document.getElementById('cart-gift-message');
    if (giftMessage && giftMessage.value.trim() === '') {
      giftMessage.focus();
      giftMessage.setCustomValidity('Please write your gift message before checking out.');
      giftMessage.reportValidity();
      return;
    }

    if (giftMessage) giftMessage.setCustomValidity('');

    evt.preventDefault();
    evt.stopPropagation();

    getCart().then(function (cart) {
      const result = validateCart(cart);

      if (result === true) {
        window.location.pathname = '/checkout';
      } else {
        showValidationModal(result);
      }
    }).catch(function (err) {
      console.error('Gift box notice: error fetching cart', err);
      window.location.pathname = '/checkout';
    });
  }

  // ─── Cart drawer checkout handler ───────────────────────────────────────────

  function drawerCheckoutHandler(evt) {
    const giftMessage = document.getElementById('cart-drawer-gift-message');

    if (giftMessage && giftMessage.value.trim() === '') {
      evt.preventDefault();
      evt.stopPropagation();
      giftMessage.focus();
      giftMessage.setCustomValidity('Please write your gift message before checking out.');
      giftMessage.reportValidity();
      return;
    }

    if (giftMessage) giftMessage.setCustomValidity('');

    evt.preventDefault();
    evt.stopPropagation();

    getCart().then(function (cart) {
      const result = validateCart(cart);

      if (result === true) {
        window.location.pathname = '/checkout';
      } else {
        showValidationModal(result);
      }
    }).catch(function (err) {
      console.error('Gift box notice: drawer checkout error', err);
      window.location.pathname = '/checkout';
    });
  }

  // ─── Post-ATC validation ─────────────────────────────────────────────────────

  function bindPostAtcValidation() {
    document.addEventListener('dispatch:cart-drawer:refresh', function () {
      // Repopulate drawer gift message after AJAX re-render
      repopulateGiftMessageFields();

      getCart().then(function (cart) {
        const result = validateCart(cart);
        if (result !== true && result !== 'too few items') {
          showValidationModal(result);
        }
      }).catch(function (err) {
        console.error('Gift box notice: post-ATC validation error', err);
      });
    });
  }

  // ─── Bind drawer events ──────────────────────────────────────────────────────

  function bindDrawerEvents() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    cartDrawer.addEventListener('click', function (evt) {
      const btn = evt.target.closest('button[name="checkout"]');
      if (!btn) return;
      drawerCheckoutHandler(evt);
    });

    cartDrawer.addEventListener('input', function (evt) {
      if (evt.target.id === 'cart-drawer-gift-message') {
        evt.target.setCustomValidity('');
        saveGiftMessageAttribute(evt.target.value);
      }
    });
  }

  // ─── Cart summary observer ───────────────────────────────────────────────────

  function bindCartSummaryObserver() {
    const cartSummary = document.getElementById('cart-summary');
    if (!cartSummary) return;

    const observer = new MutationObserver(function () {
      repopulateGiftMessageFields();
    });

    observer.observe(cartSummary, { childList: true, subtree: true });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    if (!window.theme || !window.theme.gift_box) return;

    // Cart page: intercept checkout button
    const checkoutBtn = document.querySelector('#cart button[name="checkout"]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', checkoutHandler);
    }

    // Cart page: use delegation so listener survives cart summary re-renders
    document.addEventListener('input', function (evt) {
      if (evt.target.id === 'cart-gift-message') {
        evt.target.setCustomValidity('');
        saveGiftMessageAttribute(evt.target.value);
      }
    });

    // Cart page: initialise field from localStorage
    const giftMessage = document.getElementById('cart-gift-message');
    if (giftMessage && savedGiftMessage !== '') {
      giftMessage.value = savedGiftMessage;
      // Ensure cart attribute is in sync on page load
      saveGiftMessageAttribute(savedGiftMessage);
    }

    // Cart page: repopulate after cart summary re-renders
    bindCartSummaryObserver();

    // Cart drawer: bind via delegation
    bindDrawerEvents();

    // Post-ATC validation and drawer repopulation
    bindPostAtcValidation();

    // Hidden modal trigger
    const trigger = document.querySelector('.gift-box-notice-trigger');
    if (trigger) {
      trigger.addEventListener('click', function () {
        openGiftBoxModal();
      });
    }

    // Repopulate any visible fields on init
    repopulateGiftMessageFields();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();